/**
 * Hear Control — shared config (storage keys, env, API base).
 * Each STAGE points at its own hear-core execute-api (dev / prod). Empty base → mock.
 */

export const STORAGE_KEYS = {
  LOGGED_IN: "hear_logged_in",
  USER_EMAIL: "hear_user_email",
  USER_NAME: "hear_user_name",
  USER_CRFA: "hear_user_crfa",
  USER_PERMISSION: "hear_user_permission",
  USER_STATUS: "hear_user_status",
  USER_ID: "hear_user_id",
  COMPANY_ID: "hear_company_id",
  ACCESS_TOKEN: "hear_access_token",
  ACCESS_TOKEN_EXPIRES_AT: "hear_access_token_expires_at",
  ID_TOKEN: "hear_id_token",
  ID_TOKEN_EXPIRES_AT: "hear_id_token_expires_at",
  REFRESH_TOKEN: "hear_refresh_token",
  REMEMBER_ME: "hear_remember_me",
  AUTH_REFRESH_RETRIES: "hear_auth_refresh_retries",
};

/** Far-future expiry used by mock login (ISO string). */
export const MOCK_TOKEN_EXPIRY_MS = 1000 * 60 * 60 * 24 * 365;

/**
 * @param {string} [hostname]
 * @returns {"dev"|"prod"}
 */
export function detectEnvironment(hostname = globalThis.location?.hostname || "") {
  const host = String(hostname || "").toLowerCase();
  if (!host || host === "localhost" || host === "127.0.0.1") {
    return "dev";
  }
  if (host.includes("hear-dev") || host.includes("staging") || host.includes("desenvolvimento")) {
    return "dev";
  }
  if (host === "hear.leadcontrol.ia.br" || host.startsWith("hear.")) {
    return "prod";
  }
  return "prod";
}

/**
 * Hear execute-api only — never Lead Control URLs. No trailing slash.
 * prod = `ServiceEndpoint` of `hear-core-prod` (no `api-hear` alias yet).
 */
export const API_CONFIG = {
  dev: "https://tbhttb97xd.execute-api.us-east-1.amazonaws.com/dev",
  prod: "https://qh467w5ep2.execute-api.us-east-1.amazonaws.com/prod",
};

/** LC MPA origin for “Abrir no Lead Control” (funil). */
export const LC_APP_BASE = {
  dev: "https://dev.leadcontrol.ia.br",
  prod: "https://app.leadcontrol.ia.br",
};

/** Stages where Configurações → Integrações shows the Lead Control card. */
export const LEAD_CONTROL_INTEGRATION_UI_STAGES = ["dev", "prod"];

/**
 * Nav `data-page` stems hidden on production hosts (mock / fixture pages).
 * Remove a stem when that screen is usable in prod. Dev/localhost stay unfiltered.
 */
export const NAV_HIDDEN_IN_PROD = Object.freeze([
  "painel",
  "agenda",
  "audio_fono",
  "financeiro",
  "precificacao",
  "pagamentos",
  "emissor_nf",
  "manutencao",
  "relatorios",
]);

const NAV_HIDDEN_IN_PROD_SET = new Set(NAV_HIDDEN_IN_PROD);

/**
 * @param {string} navStem
 * @returns {boolean}
 */
export function isNavHiddenInProd(navStem) {
  const stem = String(navStem || "").trim().toLowerCase();
  return NAV_HIDDEN_IN_PROD_SET.has(stem);
}

const STAGE = detectEnvironment();

export const CONFIG = {
  STORAGE_KEYS,
  APP_NAME: "Hear Control",
  STAGE,
  API_BASE_URL: API_CONFIG[STAGE] || "",
  DEFAULT_LANDING: "pages/atendimento.html",
  LOGIN_PATH: "pages/login.html",
  AUTH_REFRESH_THRESHOLD_MINUTES: 5,
  AUTH_REFRESH_RETRY_MAX: 3,
  NETWORK_RETRY: {
    MAX_ATTEMPTS: 3,
    BASE_DELAY_MS: 500,
    RETRYABLE_STATUSES: [502, 503, 504],
  },
  FEATURE_FLAGS: {
    CADASTRO_CATALOG: true,
    CADASTRO_DISCOUNTS: true,
    /** Off until BE stubs /organizations (F9 gap — mock page kept in repo). */
    CADASTRO_ORGANIZATIONS: false,
    /** Live GET/PATCH `/configuration/users` = hear-backend 010 (014 closeout). */
    CONFIGURATION_USERS_LIVE: true,
    /**
     * Live GET/PUT `/configuration/access-policy*` = hear-backend overlay (019).
     * MUST NOT reuse CONFIGURATION_USERS_LIVE.
     */
    CONFIGURATION_ACCESS_POLICY_LIVE: true,
    /** Live GET/PUT `/configuration/organization` = hear-backend 012 (017 closeout). */
    CONFIGURATION_ORGANIZATION_LIVE: true,
    /**
     * Live GET/PUT `/configuration/vocabulary` = hear-backend **017**, mapped
     * at the FE boundary to aba keys (`fonteOrigem` / `campanhasDescontos` / …).
     * Flag false → mock `/configuration/parameters`.
     * MUST NOT reuse CONFIGURATION_ORGANIZATION_LIVE or ACCESS_POLICY_LIVE.
     */
    CONFIGURATION_PARAMETERS_LIVE: true,
    /**
     * Live `GET /catalog/summary` = hear-backend 011 (present in `dev`).
     * Kill-switch: set false if execute-api still treats `summary` as `{sku}`.
     * Mock (empty API_BASE_URL) always fetches the path.
     */
    CATALOG_SUMMARY_LIVE: true,
    /**
     * Live `/catalog/imports*` = hear-backend **015** (presign → S3 → worker).
     * Requires `API_BASE_URL`. Flag false or empty API → mock adapter only.
     * MUST NOT reuse CATALOG_SUMMARY_LIVE.
     */
    CATALOG_IMPORT_LIVE: true,
    /**
     * Live `/partners*` = hear-backend **016**. Kill-switch false → mock.
     * On when `dev` hc_api has `/partners*` (hear-backend **016**). Kill-switch false → mock.
     * MUST NOT reuse CADASTRO_ORGANIZATIONS.
     */
    PARTNERS_LIVE: true,
    /**
     * Live `/partners/imports*` = hear-backend **020** (presign → S3 → worker).
     * Requires `API_BASE_URL`. Kill-switch false → mock `/partners/import*`.
     * MUST NOT reuse PARTNERS_LIVE.
     */
    PARTNERS_IMPORT_LIVE: true,
    /**
     * Live `/registros*` = hear-backend **018**.
     * On when `dev` hc_api has `/registros*`. Kill-switch false → mock.
     * MUST NOT reuse PARTNERS_LIVE / CONFIGURATION_PARAMETERS_LIVE.
     */
    REGISTROS_LIVE: true,
    /**
     * Live `GET /patients/{id}/events` (Bloco 4 do prontuário) = hear-backend **004**.
     * Kill-switch false → mock adapter paginado (`eventosCadastro`).
     * MUST NOT reuse REGISTROS_LIVE.
     */
    PATIENT_EVENTS_LIVE: true,
    /**
     * Configurações → Integrações: card Lead Control (pair/disconnect).
     * Off → «Em breve». Plan gating stays on the Lead Control side.
     */
    LEAD_CONTROL_INTEGRATION_UI: LEAD_CONTROL_INTEGRATION_UI_STAGES.includes(STAGE),
  },
  /** LC funil origin for the stage (dev → dev.leadcontrol, prod → app.leadcontrol). */
  LEAD_CONTROL_APP_BASE: LC_APP_BASE[STAGE] || "",
  PAGES: {
    LOGIN: "pages/login.html",
    PAINEL: "pages/painel.html",
  },
};

/**
 * @returns {boolean}
 */
export function isApiConfigured() {
  return Boolean(String(CONFIG.API_BASE_URL || "").trim());
}

export default CONFIG;
